import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { 
  Package, Search, Filter, Eye, RefreshCw,
  Clock, CheckCircle, Truck, AlertCircle, XCircle,
  ChevronRight, BarChart3, DollarSign, ShoppingCart, Loader2,
  Settings
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('kaha_session');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', icon: Clock, color: 'status-pending' },
  in_review: { label: 'En revisión', icon: Eye, color: 'status-in_review' },
  in_production: { label: 'En producción', icon: Truck, color: 'status-in_production' },
  completed: { label: 'Completado', icon: CheckCircle, color: 'status-completed' },
  cancelled: { label: 'Cancelado', icon: XCircle, color: 'status-cancelled' },
};

const STAGE_CONFIG = {
  diseno: { label: 'Diseño', color: 'stage-diseno' },
  impresion: { label: 'Impresión', color: 'stage-impresion' },
  suaje: { label: 'Suaje', color: 'stage-suaje' },
  pegado: { label: 'Pegado', color: 'stage-pegado' },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const ordersRes = await axios.get(`${API}/orders`);
      setOrders(ordersRes.data);
    } catch {
      toast.error('Error al cargar ordenes');
    }
    try {
      const statsRes = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(statsRes.data);
    } catch (err) { console.warn('Stats fetch skipped:', err.message); }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateOrderStatus = async (orderId, newStatus, newStage = null) => {
    try {
      let url = `${API}/orders/${orderId}/status?status=${newStatus}`;
      if (newStage) url += `&production_stage=${newStage}`;
      
      await axios.patch(url);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
      console.warn('Status update error:', error.message);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background font-manrope">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2">
              <Package className="w-8 h-8 text-accent" />
              <span className="text-xl font-bold text-primary">KAHA</span>
            </a>
            <span className="text-sm text-muted-foreground border-l pl-4">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/admin/costos')} 
              variant="outline"
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Panel de Costos
            </Button>
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
              className="rounded-full"
            >
              Ver tienda
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="shadow-soft">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Órdenes</p>
                  <p className="text-2xl font-bold" data-testid="stat-total-orders">{stats.total_orders}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                  <p className="text-2xl font-bold">{stats.pending_orders}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En Producción</p>
                  <p className="text-2xl font-bold">{stats.in_production}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completados</p>
                  <p className="text-2xl font-bold">{stats.completed_orders}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ingresos</p>
                  <p className="text-xl font-bold" data-testid="stat-revenue">
                    ${stats.total_revenue.toLocaleString('es-MX')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="shadow-soft mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Buscar por cliente, email o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-orders"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="btn-refresh">
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card className="shadow-soft">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent" />
              Órdenes ({filteredOrders.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay órdenes que mostrar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 text-sm">
                    <tr>
                      <th className="text-left p-4 font-semibold">ID</th>
                      <th className="text-left p-4 font-semibold">Cliente</th>
                      <th className="text-left p-4 font-semibold">Dimensiones</th>
                      <th className="text-left p-4 font-semibold">Cantidad</th>
                      <th className="text-left p-4 font-semibold">Total</th>
                      <th className="text-left p-4 font-semibold">Estado</th>
                      <th className="text-left p-4 font-semibold">Etapa</th>
                      <th className="text-left p-4 font-semibold">Pago</th>
                      <th className="text-left p-4 font-semibold">Fecha</th>
                      <th className="text-left p-4 font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredOrders.map((order) => {
                      const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                          <td className="p-4">
                            <span className="font-mono text-xs">{order.id.slice(0, 8)}</span>
                          </td>
                          <td className="p-4">
                            <p className="font-medium">{order.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                          </td>
                          <td className="p-4 text-sm">
                            {order.dimensions.length}×{order.dimensions.width}×{order.dimensions.height}
                          </td>
                          <td className="p-4">{order.quantity.toLocaleString()}</td>
                          <td className="p-4 font-semibold">
                            ${order.total_price.toLocaleString('es-MX')}
                          </td>
                          <td className="p-4">
                            <span className={`stage-badge ${statusConfig.color} inline-flex items-center gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="p-4">
                            {order.production_stage ? (
                              <span className={`stage-badge ${STAGE_CONFIG[order.production_stage]?.color || ''}`}>
                                {STAGE_CONFIG[order.production_stage]?.label || order.production_stage}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`stage-badge ${order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {order.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="p-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedOrder(order)}
                              className="gap-1"
                              data-testid={`btn-view-${order.id.slice(0,8)}`}
                            >
                              Ver
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedOrder(null)}>
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-floating" onClick={e => e.stopPropagation()}>
              <CardHeader className="border-b sticky top-0 bg-white z-10">
                <div className="flex items-center justify-between">
                  <CardTitle>Detalle de Orden</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)}>
                    <XCircle className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Order Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">ID de Orden</p>
                    <p className="font-mono">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Fecha</p>
                    <p>{formatDate(selectedOrder.created_at)}</p>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Cliente</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Nombre</p>
                      <p>{selectedOrder.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p>{selectedOrder.customer_email}</p>
                    </div>
                    {selectedOrder.customer_phone && (
                      <div>
                        <p className="text-muted-foreground">Teléfono</p>
                        <p>{selectedOrder.customer_phone}</p>
                      </div>
                    )}
                    {selectedOrder.delivery_address && (
                      <div className="col-span-2">
                        <p className="text-muted-foreground">Dirección</p>
                        <p>{selectedOrder.delivery_address}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Info */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Producto</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Dimensiones</p>
                      <p>{selectedOrder.dimensions.length} × {selectedOrder.dimensions.width} × {selectedOrder.dimensions.height} mm</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Material</p>
                      <p>{selectedOrder.material.type === 'single_wall' ? 'Pared sencilla' : 'Doble pared'} {selectedOrder.material.thickness}mm</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Impresión</p>
                      <p>{selectedOrder.printing.has_printing ? `Sí (${selectedOrder.printing.coverage_percent}%)` : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Cantidad</p>
                      <p>{selectedOrder.quantity.toLocaleString()} cajas</p>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Precio</h4>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Precio por unidad</span>
                    <span>${selectedOrder.unit_price.toFixed(2)} MXN</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="text-xl font-bold text-accent">
                      ${selectedOrder.total_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                    </span>
                  </div>
                </div>

                {/* Status Management */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Gestión de Estado</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Estado de Orden</p>
                      <Select 
                        value={selectedOrder.status} 
                        onValueChange={(val) => updateOrderStatus(selectedOrder.id, val)}
                      >
                        <SelectTrigger data-testid="select-order-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Etapa de Producción</p>
                      <Select 
                        value={selectedOrder.production_stage || 'none'}
                        onValueChange={(val) => updateOrderStatus(selectedOrder.id, 'in_production', val === 'none' ? null : val)}
                      >
                        <SelectTrigger data-testid="select-production-stage">
                          <SelectValue placeholder="Sin etapa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin etapa</SelectItem>
                          {Object.entries(STAGE_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
